'use client';

import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import { modalActionButtonStyles } from '@/app/components/Modal/styles';
import { Spinner } from '@/app/components/Spinner';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { RadioButton } from '@/app/components/buttons/RadioButton';
import { Search } from '@/app/components/form/Search';
import { CLIENT_ID_QUERY_KEY } from '@/consts/const';
import { getAirRequestPath } from '@/consts/paths-utils';
import { useDebouncedValue } from '@/hooks/useDebounceValue';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import type { AirRequestData } from '@/types/airRequestApiTypes';
import { useGetAirRequestsInfiniteQuery } from '../../../../hooks/useGetAirRequestsInfiniteQuery';
import { DeleteDraftModal } from './DeleteDraftModal';
import BackArrowButton from '/public/icons/back-arrow-button.svg';
import FileIcon from '/public/icons/file.svg';
import LockIcon from '/public/icons/lock.svg';
import NotFoundIcon from '/public/icons/not-found.svg';
import TrashIcon from '/public/icons/trash.svg';

interface SelectDraftModalProps {
  onGoBack: () => void;
  clientId?: number;
}

interface DraftItemProps {
  draft: AirRequestData;
  onSelect: () => void;
  isSelected: boolean;
  setIsDeleteDraftModalOpen: (isOpen: boolean) => void;
}

const DraftItem: React.FC<DraftItemProps> = ({ draft, onSelect, isSelected, setIsDeleteDraftModalOpen }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const itemContent = (
    <div
      className={clsx('flex items-center py-2 rounded-md relative group/tooltip-trigger', {
        'cursor-not-allowed': draft.is_locked,
        'cursor-pointer': !draft.is_locked,
      })}
      onClick={!draft.is_locked ? onSelect : undefined}
    >
      <div className="flex-shrink-0">
        {draft.is_locked ? (
          <div className="px-[7.44px] py-[4.25px]">
            <LockIcon className="w-[20px] h-[26px] fill-current text-neutral-700" />
          </div>
        ) : (
          <FileIcon className="w-[34px] h-[34px] stroke-current text-black" />
        )}
      </div>
      <div className="flex-1 flex-col gap-[2px] ml-[12px] mr-[8px]">
        <p className={clsx('text-button-medium text-black', draft.is_locked ? 'opacity-30' : '')}>
          {draft.identity} {draft.airports && `| ${draft.airports}`}
        </p>
        <p className={clsx('text-body-small text-grey-700', draft.is_locked ? 'opacity-30' : '')}>
          Last edited on {formatDate(draft.updated_at)}
        </p>
      </div>
      <button
        className={clsx('ml-auto', {
          'cursor-pointer': !draft.is_locked,
          'cursor-not-allowed': draft.is_locked,
        })}
        onClick={(e) => {
          e.stopPropagation();
          if (draft.is_locked) return;
          setIsDeleteDraftModalOpen(true);
          onSelect();
        }}
        disabled={draft.is_locked}
      >
        <TrashIcon
          className={clsx('w-[22px] h-[22px] fill-current', {
            'text-red-500': !draft.is_locked,
            'text-neutral-700': draft.is_locked,
          })}
        />
      </button>
      <div className="ml-4">
        <RadioButton checked={isSelected} disabled={draft.is_locked} onChange={onSelect} id={`draft-${draft.id}`} />
      </div>
      {draft.is_locked && draft.locked_by && (
        <div className="absolute -bottom-5 right-0 whitespace-nowrap rounded-md border border-grey-200 bg-white px-[12px] py-[6px] text-body-small text-black invisible opacity-0 group-hover/tooltip-trigger:visible group-hover/tooltip-trigger:opacity-100 transition-opacity duration-150 z-20 shadow-[0px_0px_6px_0px_rgba(6,32,96,0.12)]">
          {`Currently edited by ${draft.locked_by}`}
        </div>
      )}
    </div>
  );

  return itemContent;
};

export const SelectDraftModal: React.FC<SelectDraftModalProps> = ({ onGoBack, clientId: clientIdParam }) => {
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchQuery();
  const clientId = clientIdParam ?? searchParams.get(CLIENT_ID_QUERY_KEY);
  const [selectedDraft, setSelectedDraft] = useState<number | null>(null);
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);
  const router = useRouter();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data: airRequests,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAirRequestsInfiniteQuery({
    status: 1, // Only drafts
    search: debouncedSearch || undefined,
    clientId: clientIdParam,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;
    const currentLoadMoreRef = loadMoreRef.current;

    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef && observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current.unobserve(currentLoadMoreRef);
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleContinue = () => {
    if (clientId && selectedDraft) {
      router.push(getAirRequestPath(String(selectedDraft), String(clientId)));
    }
  };

  const isLoading = isPending;
  const isNoResults = !isLoading && airRequests.length === 0;

  if (isDeleteDraftModalOpen) {
    return (
      <DeleteDraftModal
        onSelectDraft={() => setSelectedDraft(null)}
        draftId={selectedDraft}
        onGoBack={() => setIsDeleteDraftModalOpen(false)}
      />
    );
  }

  return (
    <div className="py-6 md:py-[46px] px-4 md:px-[36px] flex flex-col items-center w-full md:w-[650px]">
      <h2 className="light text-blue-900 text-center">Select a draft</h2>
      <p className="md:text-body-regular text-body-small text-grey-700 text-center">
        Use a saved draft to fill in details quickly
      </p>

      <div className="flex flex-col gap-4 w-full mt-6 md:mt-[34px]">
        <Search value={search} onChange={onSearchChange} name="search" placeholder="Search draft" />

        <div className="flex flex-col gap-[2px] w-full max-h-[298px] md:min-h-[298px] overflow-y-auto">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center min-h-[160px] md:min-h-[298px]">
              <Spinner />
            </div>
          ) : isNoResults ? (
            <div className="flex-1 flex items-center justify-center min-h-[160px] md:min-h-[298px]">
              <div className="flex gap-[24px] flex-col items-center justify-center w-full">
                <NotFoundIcon />
                <div className="flex flex-col items-center justify-center gap-2">
                  <h3 className="light text-black">{debouncedSearch ? 'No matching results' : 'No drafts found'}</h3>
                  <p className="text-body-regular text-black text-center">
                    {debouncedSearch
                      ? 'We couldn\'t find any matches for your search. Try a different keyword or check your spelling.'
                      : 'You don\'t have any draft air requests yet.'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {airRequests.map((draft: AirRequestData) => (
                <DraftItem
                  key={draft.id}
                  draft={draft}
                  onSelect={() => setSelectedDraft(draft.id)}
                  isSelected={selectedDraft === draft.id}
                  setIsDeleteDraftModalOpen={setIsDeleteDraftModalOpen}
                />
              ))}

              {hasNextPage && (
                <div ref={loadMoreRef} className="h-6 flex items-center justify-center">
                  {isFetchingNextPage && <Spinner />}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-4 md:gap-[20px]">
          <BaseButton
            intent="secondary"
            onClick={onGoBack}
            size="full"
            className={modalActionButtonStyles()}
            startIcon={<BackArrowButton className="text-blue-300 fill-current" />}
          >
            Go Back
          </BaseButton>
          <BaseButton
            intent="primary"
            onClick={handleContinue}
            size="full"
            className={modalActionButtonStyles()}
            disabled={!selectedDraft || isLoading}
          >
            Continue
          </BaseButton>
        </div>
      </div>
    </div>
  );
};
